import React from "react";
import { useTranslation } from "react-i18next";
import Badge from "../../components/badge";
import BranchSelect from "../../components/branchSelect";

interface Branch {
  id: number;
  name: string;
}

export interface GenericTableHeaderProps {
  // Core props
  title?: string;
  titleTranslationKey?: string; // Optional translation key for title
  totalCount: number;
  status?: string; // PENDING | ACTIVE | undefined

  // Badge customization
  showBadge?: boolean;
  badgeVariant?: "pending" | "active" | "default";
  customBadgeText?: string; // Override default badge text
  singularLabel?: string; // e.g., "user", "request", "tenant"
  pluralLabel?: string; // e.g., "users", "requests", "tenants"

  // Layout options
  layout?: "basic" | "justified"; // basic: flex gap-2, justified: justify-between
  showBorder?: boolean;
  borderStyle?: "gray-100" | "gray-200";
  padding?: "px-4" | "py-4" | "px-4 py-4" | "" | "px-4 pt-4";

  // Branch selector (for extended layouts)
  showBranchSelector?: boolean;
  branches?: Branch[];
  selectedBranch?: string | null;
  setSelectedBranch?: (branch: string | null) => void;
  isBranchesLoading?: boolean;
  showAllBranches?: boolean;
  branchPlaceholder?: string;

  // Additional content
  children?: React.ReactNode;

  // Style overrides
  className?: string;
  titleClassName?: string;
  badgeClassName?: string;
}

const GenericTableHeader: React.FC<GenericTableHeaderProps> = React.memo(
  ({
    // Core props
    title,
    titleTranslationKey,
    totalCount,
    status,

    // Badge customization
    showBadge = true,
    badgeVariant,
    customBadgeText,
    singularLabel = "item",
    pluralLabel = "items",

    // Layout options
    layout = "basic",
    showBorder = true,
    borderStyle = "gray-100",
    padding = "px-4",

    // Branch selector
    showBranchSelector = false,
    branches = [],
    selectedBranch,
    setSelectedBranch,
    isBranchesLoading = false,
    showAllBranches = true,
    branchPlaceholder = "All Branches",

    // Additional content
    children,

    // Style overrides
    className = "",
    titleClassName = "",
    badgeClassName = "",
  }) => {
    const { t } = useTranslation();

    // Determine badge variant based on status if not explicitly provided
    const determineBadgeVariant = () => {
      if (badgeVariant) return badgeVariant;
      if (status === "PENDING") return "pending";
      if (status === "ACTIVE") return "active";
      return "default";
    };

    // Generate badge text
    const getBadgeText = () => {
      if (customBadgeText) return customBadgeText;

      if (status === "PENDING") {
        return `${totalCount} ${t("shared.tableToolbar.pending")}`;
      }
      if (status === "ACTIVE") {
        return `${totalCount} ${t("shared.tableToolbar.active")}`;
      }

      // Default count with pluralization
      const label = totalCount === 1 ? singularLabel : pluralLabel;
      return `${totalCount} ${label}`;
    };

    // Build container classes
    const containerClasses = [
      "flex items-center",
      layout === "justified" ? "justify-between" : "gap-2",
      "pb-4",
      showBorder ? `border-b border-${borderStyle}` : "",
      padding,
      className,
    ]
      .filter(Boolean)
      .join(" ");

    // Build title classes
    const titleClasses = ["text-lg font-semibold", titleClassName]
      .filter(Boolean)
      .join(" ");

    // Build badge classes
    const badgeClasses = [
      status === "" ? "!bg-white" : "",
      "!rounded-md !font-normal",
      badgeClassName,
    ]
      .filter(Boolean)
      .join(" ");

    const displayTitle = titleTranslationKey
      ? t(titleTranslationKey)
      : title || "";

    return (
      <div className={containerClasses}>
        {/* Left side: Title and Badge */}
        <div className="flex items-center gap-2">
          <h1 className={titleClasses}>{displayTitle}</h1>
          {showBadge && (
            <Badge variant={determineBadgeVariant()} className={badgeClasses}>
              {getBadgeText()}
            </Badge>
          )}
        </div>

        {/* Right side: Branch selector and additional content */}
        {(showBranchSelector || children) && (
          <div className="flex items-center gap-2">
            {showBranchSelector && setSelectedBranch && (
              <BranchSelect
                isLoading={isBranchesLoading}
                branches={branches}
                selectedBranch={
                  selectedBranch ? parseInt(selectedBranch) : null
                }
                onBranchChange={(branchId) =>
                  setSelectedBranch(branchId?.toString() || null)
                }
                showAllBranches={showAllBranches}
                placeholder={branchPlaceholder}
                className="w-[324px] h-10 px-3 py-2 text-sm border border-[#D5D7DA] rounded-lg bg-white focus:outline-none"
              />
            )}
            {children}
          </div>
        )}
      </div>
    );
  }
);

GenericTableHeader.displayName = "GenericTableHeader";

export default GenericTableHeader;
